import {
  ActionIcon,
  Anchor,
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Menu, Pagination,
  Paper, Skeleton,
  Table,
  Text,
  Tooltip,
  UnstyledButton,
  useMantineTheme
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconArrowsDiagonal,
  IconCamera,
  IconChecklist,
  IconChevronDown,
  IconChevronRight,
  IconDots,
  IconDragDrop,
  IconEye,
  IconShirt,
  IconSubtask,
  IconTrash,
} from "@tabler/icons-react";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { getColumnVisibility } from "core/components/columnSelectPopover/ColumnSelectPopover";
import { PrimaryBadge } from "core/components/badge/PrimaryBadge";
import { CountDisplay } from "core/components/countDisplay/CountDisplay";
import { PulseIndicator } from "core/components/index";
import TalentPopover from "core/components/popovers/TalentPopover";
import Th from "core/components/table/Thead";
import { useScrollTracker } from "core/hooks/useScrollTracker";

import { SampleEventType, SampleStatusTypes } from "core/models/SampleModel";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import Moment from "react-moment";
import { Link, useNavigate } from "react-router-dom";

// import { useSortBy, useTable, usePagination, useFilters, useGlobalFilter, useExpanded } from 'react-table';
import { AppConstants, DataFormats } from "utils/constants";
import {
  getCommonPinningStyles,
  getStyleStatusColor,
  getUserAvatarColour,
  getUserInitials,
} from "utils/helper";
import { logger } from "utils/logger";
import TextHighlight from "core/components/textHighlight/TextHighlight";

const SCROLL_REF = 'wiplist-scrollPos';

const WipList = ({
  originalData,
  data,
  onItemClick,
  onMenuItemClick,
  onStatusClick,
  onItemSelected,
  selectedItems,
  filter,
  actionMenu,
  columns = [],
  columnVisibility,
  rowMenuItems,
  showPopover = false,
  allowAssignCapture = false,
  totalCount = 0,
  loading = false,
  paginationSize = 100,
  setFilter,
  showPagination = true,
}) => {

  const [grouping, setGrouping] = useState([]);
  const [expanded, setExpanded] = useState({ ...filter?.groupExpansion });

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 500,
  });

  const [columnPinning, setColumnPinning] = useState({
    left: filter?.groupBy?.length ? [] : ["seasonSort"],
    // right: ["action"],
  });

  useEffect(() => {
    if (filter?.groupBy?.length) {
      setGrouping([filter?.groupBy]);
      setColumnPinning({
        ...columnPinning,
        left: [filter?.groupBy],
      });
    } else {
      setGrouping([]);
      setColumnPinning({
        ...columnPinning,
        left: ["seasonSort"],
      });
    }
  }, [filter?.groupBy]);

  useEffect(() => {
    if(setFilter)
      setFilter({ ...filter, groupExpansion: { ...expanded } });
  }, [expanded]);

  // The grouped column carries the group rows, so it's never hidden.
  const groupBy = filter?.groupBy;
  const tableProps = {
    columns,
    columnVisibility: groupBy
      ? { ...(columnVisibility ?? getColumnVisibility(columns)), [groupBy]: true }
      : columnVisibility,
    data,
    grouping: grouping,
    setGrouping,
    loading,
    selectedItems,
    pagination,
    setPagination,
    columnPinning,
    onItemClick,
    showPagination,
    expanded,
    setExpanded,
    originalData,
    keyword: filter?.keyword || ""
  };

  const divRef = useRef();
  const {scrolTop} = useScrollTracker(divRef,'wiplist');

  // Load the scroll position if it has been previously stored
  useEffect(() => {
    const scrollPosition = localStorage.getItem(SCROLL_REF);

    if (scrollPosition) {
      divRef.current.scrollTo(0, parseInt(scrollPosition, 10));
    } else {
      divRef.current.scrollTo(0, 0);
    }
  }, []);

  return (
    <Box
      ref={divRef}
      style={{ width: "100%", overflowX: "auto", height: "100%" }}
    >
      <CustomTable {...tableProps} />
    </Box>
  );
};

const CustomTable = ({
  columns,
  data,
  grouping,
  setGrouping,
  loading,
  selectedItems,
  onItemClick,
  onMenuItemClick,
  onItemSelected,
  onStatusClick,
  actionMenu,
  showPopover,
  allowAssignCapture,
  pagination,
  setPagination,
  columnPinning,
  showPagination,
  expanded,
  setExpanded,
  originalData,
  keyword,
  columnVisibility,
}) => {
  // Use the state and functions returned from useTable to build your UI

  const theme = useMantineTheme();
  const mobileMatch = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  // const groupedRows = table.getPrePaginationRowModel().rows;

  // Then calculate total rows counting expanded groups
  // const getPageCount = (rows) => {
  //   return (rows.length/pagination.pageSize)+1
  // };

  const getPageCount = (rows) => {
    if (!rows.length || !pagination.pageSize) return 0;
    return Math.ceil(rows.length / pagination.pageSize);
  };

  // Calculate pageCount
  // const totalRows = getTotalRows(groupedRows);
  // const pageCount = Math.ceil(totalRows / pageSize);

  const table = useReactTable({
    data: data,
    columns: columns,
    // pageCount: Math.ceil(data.length / pagination.pageSize) ?? -1,
    // pageCount: Math.ceil(getTotalRows(table.getPrePaginationRowModel().rows) / pagination.pageSize),
    rowCount: data.length,
    autoResetExpanded: false,

    // onColumnPinningChange: setColumnPinning,
    // onSortingChange: setSorting,
    // onGroupingChange: setGrouping,
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),

    onGroupingChange: setGrouping,
    onExpandedChange: setExpanded,
    // onPaginationChange: onPagChange,
    state: {
      columnPinning,
      // Controlled by the column picker; defaults come from meta.show.
      columnVisibility: columnVisibility ?? getColumnVisibility(columns),
      // sorting,
      grouping,
      pagination,
      expanded,
    },
    onPaginationChange: setPagination,
    manualPagination: false,
    debugTable: false,
    defaultColumn: {
      size: 150, //starting column size
      minSize: 100, //enforced during column resizing
      maxSize: 500, //enforced during column resizing
    },
  });

  // logger.log(table.getHeaderGroups())

  const getCell = (cell, row) => {
    var cellRender;

    switch (cell.column.id) {
      // case "status": {
      //   const active =
      //     cell.getValue() === "active" || cell.getValue() === "true"
      //       ? true
      //       : false;
      //   cellRender = (
      //     <Badge color={active ? "green" : "gray"}>{cell.getValue()}</Badge>
      //   );
      //   break;
      // }
      case "otbDate":
      case "distributionDate":
      case "completed": {
        cellRender = (
          <Group sx={{ width: 120 }}>
            <Text size="sm">
              {cell.getValue() && cell.getValue().length ? (
                <Moment
                  date={cell.getValue()}
                  parse="YYYY-MM-DD-hh-mm-ss"
                  format="DD/MM/YYYY"
                  trim
                />
              ) : (
                ""
              )}
            </Text>
          </Group>
        );
        break;
      }
      case "styleNumber": {
        const styleValue = cell.getValue();
        cellRender = (
          <Link className="router-link" to={`/distributions/${styleValue}`}>
            <Group justify="space-between">
              <TextHighlight text={styleValue} keyword={keyword} />
            </Group>
          </Link>
        );
        break;
      }

      case "status": {
        cellRender = (
         <>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </>
        );
        break;
      }

      default: {
        const cellValue = cell.getValue();
        const displayValue = typeof cellValue === 'string' || typeof cellValue === 'number' 
          ? cellValue 
          : flexRender(cell.column.columnDef.cell, cell.getContext());
        
        cellRender = (
          <Text size="sm" truncate>
            {typeof displayValue === 'string' || typeof displayValue === 'number' ? (
              <TextHighlight text={displayValue} keyword={keyword} />
            ) : (
              displayValue
            )}
          </Text>
        );
        break;
      }
    }
    return cellRender;
  };

  const numbers = Array(50)
    .fill()
    .map((_, i) => i + 1);

  return (
    <div className="h-scroll-table-container" style={{ height: "100%" }}>
      <Table
        withRowBorders
        withColumnBorders
        highlightOnHover
        size="sm"
        style={{ marginBottom: 0 }}
        className="h-scroll-table"
        stickyHeader
        stickyHeaderOffset={0}
      >
        <Table.Thead
        // className={
        //   scrolled ? ["scrollTableHeader scrolled"] : ["scrollTableHeader"]
        // }
        >
          {table.getHeaderGroups().map((headerGroup, idx) => {
            // const { key, ...headerProps } = { ...headerGroup.getHeaderGroupProps() };
            return (
              <Table.Tr
                key={idx}
                // {...headerProps}
              >
                {headerGroup.headers.map((header, i) => {
                  const { column } = header;
                 
                  return (
                    // Add the sorting props to control sorting. For this example
                    // we can add them into the header props
                    <Th
                      column={column}
                      onSort={() => logger.log("onsort")}
                      key={i}
                      {...{
                        onClick: header.column.getToggleSortingHandler(),
                        reversed:
                          header.column.getIsSorted() === "asc" ? false : true,
                        // onClick: header.column.getToggleGroupingHandler(),
                        style: {
                          cursor: "pointer",
                          minWidth: header.column.getIsGrouped()
                            ? 350
                            : `${header.getSize()}px`,
                        },
                        className: ["scrollTableHeader", "scrolled"]
                      }}
                      sorted={header.column.getIsSorted() ? true : undefined}
                    >
                      {/* // <Th key={header.id} colSpan={header.colSpan}> */}
                      <div>
                        {header.isPlaceholder ? null : (
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none"
                                : "",
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </div>
                        )}
                      </div>
                    </Th>
                  );
                })}
              </Table.Tr>
            );
          })}
        </Table.Thead>

        {loading ? (
          <Table.Tbody>
            {numbers.map((i) => (
              <Table.Tr key={i}>
                {table.getVisibleLeafColumns().map((col, idx) => (
                  <Table.Td key={idx}>
                    <Skeleton height={8} my="sm" />
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        ) : (
          <Table.Tbody>
            {table.getRowModel().rows.map((row) => {
              let checked = selectedItems.find(
                (val) => val.id === row.original.id
              )
                ? true
                : false;

              return (
                <Table.Tr key={row.id} className={checked ? "selected" : ""}>
                  {row.getVisibleCells().map((cell,idx) => {
                    return (
                      <Table.Td
                        key={idx}
                        onClick={(e) => {
                          onItemClick(row);
                        }}
                        className={checked ? "cellChecked" : ""}
                        style={{
                          verticalAlign: "top",
                          ...getCommonPinningStyles(cell.column),
                        }}
                      >
                        {cell.getIsGrouped() ? (
                          // If it's a grouped cell, add an expander and row count
                          <>
                            <UnstyledButton
                              size={"sm"}
                              {...{
                                onClick: row.getToggleExpandedHandler(),
                                style: {
                                  cursor: row.getCanExpand()
                                    ? "pointer"
                                    : "normal",
                                },
                              }}
                            >
                              <Group>
                                {/* {row.getIsExpanded() ? '👇' : '👉'}{' '} */}
                                {row.getIsExpanded() ? (
                                  <IconChevronDown
                                    stroke={AppConstants.ICON_STROKE_WIDTH}
                                    size={AppConstants.ICON_SIZE}
                                  />
                                ) : (
                                  <IconChevronRight
                                    stroke={AppConstants.ICON_STROKE_WIDTH}
                                    size={AppConstants.ICON_SIZE}
                                  />
                                )}{" "}
                                {/* <Checkbox
                                  checked={isChecked}
                                  onClick={(e) => {
                                    logger.log(e);
                                    e.stopPropagation();
                                    // e.preventDefault();
                                  }}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    onGroupToggleCheck(
                                      e,
                                      e.currentTarget.checked,
                                      row.subRows
                                    );
                                  }}
                                /> */}
                                {cell.getValue() ? (
                                  getCell(cell, row)
                                ) : (
                                  <Text c="dimmed">No value</Text>
                                )}
                                {/* {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}{' '} */}
                                <PrimaryBadge>
                                  {row.subRows.length}
                                </PrimaryBadge>
                              </Group>
                            </UnstyledButton>
                          </>
                        ) : cell.getIsAggregated() ? (
                          // If the cell is aggregated, use the Aggregated
                          // renderer for cell
                          flexRender(
                            cell.column.columnDef.aggregatedCell ??
                              cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        ) : cell.getIsPlaceholder() ? null : ( // For cells with repeated values, render null
                          // Otherwise, just render the regular cell
                          getCell(cell, row)
                          // flexRender(
                          //   cell.column.columnDef.aggregatedCell ??
                          //     cell.column.columnDef.cell,
                          //   cell.getContext()
                          // )
                        )}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        )}
      </Table>

      {showPagination ? (
        <Paper>
          {loading ? (
            <Group className={"pagination-footer"} py="sm">
              <Skeleton height={20} width={100} />
            </Group>
          ) : (
            <Group className={"pagination-footer"} py="sm">
              <Pagination
                size={"sm"}
                // total={table.getPageCount()}
                total={getPageCount(table.getPrePaginationRowModel().rows)}
                withEdges
                withPages={mobileMatch?false:true}
                value={table.getState().pagination.pageIndex + 1}
                onChange={(idx) => {
                  logger.log(idx);
                  table.setPageIndex(idx - 1);
                }}
              />

              <Group>
                <Text size="sm">
                  Page{" "}
                  <strong>
                    {table.getState().pagination.pageIndex + 1} to{" "}
                    {getPageCount(table.getPrePaginationRowModel().rows)}
                  </strong>{" "}
                   {/* of <strong>{originalData?.length}</strong> */}
                </Text>

                {/* <Select
                              size='sm'
                              value={pagination.pageSize.toString()}
                              onChange={(e) => {
                                  // setPageSize(Number(e.target.value));
                              }}
                              data={[
                                  { value: '20', label: 'Show 20' },
                                  { value: '50', label: 'Show 50' },
                                  { value: '80', label: 'Show 80' },
                                  { value: '100', label: 'Show 100' },
                              ]}></Select> */}
              </Group>
            </Group>
          )}
        </Paper>
      ) : null}
    </div>
  );
};

export default WipList;

const SampleItemMenu = (props) => {
  const defaultMenu = [
    {
      handler: () =>
        props.onMenuItemClick({
          type: SampleEventType.SAMPLE_VIEW,
          data: props.item,
        }),
      icon: <IconEye size={14} />,
      label: "View Sample",
    },
    {
      handler: () =>
        props.onMenuItemClick({
          type: SampleEventType.SAMPLE_ASSIGN_CAPTURES,
          data: props.item,
        }),
      icon: <IconDragDrop size={14} />,
      size: 14,
      label: "Style Guide Selections",
    },
    {
      handler: () =>
        props.onMenuItemClick({
          type: SampleEventType.SAMPLE_DELETE,
          data: props.item,
        }),
      color: "red",
      icon: <IconTrash size={14} />,
      size: 14,
      label: "Unassign Sample",
    },
  ];

  const menuItems = props.actionMenu ? props.actionMenu : defaultMenu;

  return (
    <Menu position="bottom-end">
      <Menu.Target>
        <ActionIcon>
          <IconDots />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {menuItems.map((val, idx) => (
          <Menu.Item
            key={idx}
            onClick={() => val.handler(props.item)}
            icon={val.icon}
          >
            {val.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};